import React, { useEffect, useRef, useCallback } from "react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";

export default function OrderSurveyPopup({
  open,
  onClose,
  companyCode = "inzl",
}) {
  const surveyContainerRef = useRef(null);
  const surveyInstanceRef = useRef(null);

  // Survey IDs for different regions (company codes)
  const SURVEY_IDS = {
    ihal: "2YXSmEF8-Ee6dNdZNF2Qhs89a2tLAIdDvVTNpvLGVmJURDlGRERaMDE5VVlXN1A1MFRKMkpLVjdXQi4u", // AU survey
    inzl: "2YXSmEF8-Ee6dNdZNF2Qhs89a2tLAIdDvVTNpvLGVmJUN1NDNFMxTEZHT0JOS1dWS1VZRldXU0hPVS4u", // NZ survey
  };

  const initializeSurvey = useCallback(() => {
    if (
      !window.SurveyEmbed ||
      !surveyContainerRef.current ||
      surveyInstanceRef.current
    ) {
      return;
    }

    try {
      const surveyId = SURVEY_IDS[companyCode] || SURVEY_IDS["ihal"];
      const se = new window.SurveyEmbed(
        surveyId,
        "https://customervoice.microsoft.com/",
        "https://mfpembedcdnwus2.azureedge.net/mfpembedcontwus2/",
        "true"
      );

      const context = {
        "First Name": "",
        "Last Name": "",
        locale: "en",
      };

      se.renderInline(surveyContainerRef.current.id, context);
      surveyInstanceRef.current = se;
    } catch (error) {
      console.error("Failed to initialize survey:", error);
    }
  }, [companyCode]);

  useEffect(() => {
    if (!open) return;

    // Check if script already loaded
    if (window.SurveyEmbed) {
      initializeSurvey();
      return;
    }

    // Load Microsoft Forms embed scripts
    const embedScript = document.createElement("script");
    embedScript.src =
      "https://mfpembedcdnwus2.azureedge.net/mfpembedcontwus2/Embed.js";
    embedScript.type = "text/javascript";
    embedScript.id = "au-survey-embed-script";

    const embedStyles = document.createElement("link");
    embedStyles.rel = "stylesheet";
    embedStyles.type = "text/css";
    embedStyles.href =
      "https://mfpembedcdnwus2.azureedge.net/mfpembedcontwus2/Embed.css";
    embedStyles.id = "au-survey-embed-styles";

    // Add custom styles to override iframe height
    const customStyles = document.createElement("style");
    customStyles.id = "au-survey-custom-styles";
    customStyles.textContent = `
      #au-survey-content iframe {
        min-height: 500px !important;
        height: 100% !important;
      }
    `;

    document.head.appendChild(embedStyles);
    document.head.appendChild(customStyles);
    document.head.appendChild(embedScript);

    embedScript.onload = initializeSurvey;

    // Cleanup function
    return () => {
      surveyInstanceRef.current = null;
      document.querySelector("#au-survey-embed-script")?.remove();
      document.querySelector("#au-survey-embed-styles")?.remove();
      document.querySelector("#au-survey-custom-styles")?.remove();
    };
  }, [open, initializeSurvey]);

  return (
    <Dialog open={open} onClose={onClose} className="relative z-50">
      <DialogBackdrop
        transition
        className="fixed inset-0 bg-gray-600/75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
      />

      <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
        <div className="flex min-h-full items-center justify-center p-4">
          <DialogPanel
            transition
            className="relative transform overflow-hidden rounded-lg bg-white shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:my-8 sm:w-full sm:max-w-2xl"
          >
            <div className="absolute right-0 top-0 pr-4 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="rounded-md bg-white text-gray-400 hover:text-gray-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                <span className="sr-only">Close</span>
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="px-4 pb-4 pt-5 sm:p-6">
              <div className="text-center sm:mt-2">
                <DialogTitle
                  as="h3"
                  className="text-lg font-semibold leading-6 text-gray-900 mb-2"
                >
                  Help us improve your experience
                </DialogTitle>

                <p className="text-sm text-gray-600 mb-4">
                  Your prescription has been sent successfully. Please take a
                  moment to share your feedback.
                </p>

                <div
                  ref={surveyContainerRef}
                  id="au-survey-content"
                  className="min-h-[600px] w-full h-px"
                />
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  type="button"
                  onClick={onClose}
                  className="inline-flex items-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                >
                  Close
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </div>
    </Dialog>
  );
}
